import { safeUrl } from "./safeUrl";
import { API_URL } from "./api";
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import RestaurantLogo from "./RestaurantLogo";
import { landingStrings, LandingLang } from "./landingStrings";

// Defence in depth: the backend rejects non-http(s) links on write, but this
// page is public and renders admin-supplied URLs into href/src, and React does
// not sanitize URL schemes. Anything that isn't http(s) (or a site-relative
// path) is dropped rather than rendered.


const QRLanding: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [content, setContent] = useState<any>(null);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [optInError, setOptInError] = useState<string | null>(null);
  // French first, since this app's primary market, with English a tap away.
  const [lang, setLang] = useState<LandingLang>('fr');
  const s = landingStrings[lang];

  useEffect(() => {
    if (!code) return;
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/qr/${code}/landing`)
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => setContent(data))
      .catch(() => setError(landingStrings.fr.notFound))
      .finally(() => setLoading(false));
    // Log scan event. Best-effort: a guest's landing page must not break over
    // a failed scan count, so a rejection here is swallowed rather than shown.
    fetch(`${API_URL}/api/qr/${code}/scan`, { method: 'POST' }).catch(() => {});
  }, [code]);

  const handleOptIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setOptInError(null);
    try {
      const res = await fetch(`${API_URL}/api/qr/${code}/optin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, phone, consent }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const err = await res.json();
        setOptInError(err.error || s.submitFailed);
      }
    } catch {
      setOptInError(s.submitFailed);
    }
  };

  if (loading) return <div className="p-8 text-center">{landingStrings.fr.loading}</div>;
  if (error) return <div className="p-8 text-center text-red-600">{error}</div>;

  const headline = (lang === 'fr' ? content?.headlineFr : content?.headlineEn) || s.notFound;
  const description = lang === 'fr' ? content?.descriptionFr : content?.descriptionEn;
  const buttonText = lang === 'fr' ? content?.buttonTextFr : content?.buttonTextEn;
  const sponsorDescription = lang === 'fr' ? content?.sponsorDescriptionFr : content?.sponsorDescriptionEn;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6 flex flex-col items-center">
        {content?.bilingual && (
          <div className="self-end flex gap-1 mb-2">
            {(['fr', 'en'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className={`px-2 py-0.5 text-xs rounded border ${lang === l ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-300'}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        )}
        <RestaurantLogo
          logoUrl={safeUrl(content?.logoUrl)}
          logoScale={content?.logoScale}
          alt="Restaurant Logo"
          sizeClassName="w-24 h-24"
          className="mb-3"
        />
        <h1 className="text-2xl font-bold mb-1 text-center">{content?.restaurantName || "Our Restaurant"}</h1>
        <div className="mb-4 w-full text-center">
          <h2 className="text-lg font-semibold mb-1">{headline}</h2>
          <p className="text-gray-700">{description}</p>
          {content?.sponsorName && (
            <>
              <hr className="w-full border-gray-200 my-4" />
              <div>
                <p className="text-xs text-gray-500">
                  {s.sponsoredBy} <span className="font-medium text-gray-700">{content.sponsorName}</span>
                </p>
                {sponsorDescription && <p className="text-xs text-gray-500 mt-1">{sponsorDescription}</p>}
                {content?.sponsorUrl && (
                  <a
                    href={`${API_URL}/api/qr/${code}/sponsor-click`}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="inline-block mt-2 text-xs font-medium text-gray-600 border border-gray-300 rounded-full px-3 py-1 hover:bg-gray-50 hover:border-gray-400 transition"
                  >
                    {s.visitSponsor}
                  </a>
                )}
              </div>
            </>
          )}
        </div>
        {!submitted ? (
          <form onSubmit={handleOptIn} className="w-full mb-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{s.emailOptional}</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder={s.emailPlaceholder}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{s.phoneOptional}</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                placeholder={s.phonePlaceholder}
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={consent}
                onChange={e => setConsent(e.target.checked)}
                id="consent"
                className="mr-2"
                required
              />
              <label htmlFor="consent" className="text-xs text-gray-600">
                {s.consentPrefix} {content?.restaurantName || "this restaurant"}. {s.consentSuffix} {" "}
                <a
                  href={safeUrl(content?.privacyPolicyUrl) || `/privacy?lang=${lang}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-teal-600"
                >
                  {s.privacyPolicy}
                </a>.
              </label>
            </div>
            <button
              type="submit"
              className="w-full py-2 bg-teal-600 text-white rounded-md font-semibold hover:bg-teal-700 disabled:opacity-50"
              disabled={!consent}
            >
              {s.signUp}
            </button>
            {optInError && <div className="text-red-600 text-center text-sm mt-2">{optInError}</div>}
          </form>
        ) : (
          <div className="mb-4 w-full text-center text-green-600 font-semibold">
            {s.thanksReal}
          </div>
        )}
        <a
          href={safeUrl(content?.buttonUrl) || safeUrl(content?.googleReviewUrl) || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full block py-3 mt-2 bg-green-600 text-white rounded-md text-lg font-bold text-center hover:bg-green-700"
        >
          {buttonText}
        </a>
        <div className="mt-6 text-xs text-gray-400 text-center">
          {s.privacyFooter}
        </div>
      </div>
    </div>
  );
};

export default QRLanding;
